<template>
  <div :class="cardClass">
    <div class="header">
      <div class="full-width">
        <h2 class="title d-inline-block">Calls By Day of Month</h2>
        <div v-for="year in years" :key="year" class="year-button d-inline-block float-right ml-1">
          <b-button v-if="monthData" :class="{ 'selected': year === selectedYear }" @click="selectedYear = year">{{ year }}</b-button>
        </div>
      </div>
    </div>
    <div class="root">
      <div v-if="monthData">
        <bar-chart
          class="print-hide"
          :data="monthData"
          :dataset="{ borderWidth: 0 }"
          :download="false"
          :colors="['rgba(64, 121, 157, 1)']"
          height="250px"
          width="100%"
          :library="chartOptions"
        />
        <ul class="print-data-list cells print-only-inline">
          <li v-for="(v, k) in monthData" :key="k">
            {{ k }} <strong>{{ v }}%</strong>
            <span class="bar-container">
              <span class="bar" :style="{ width: (100 * v) / 100 }"></span>
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js';
import Vue from 'vue';
import Chartkick from 'vue-chartkick';

Vue.use(Chartkick.use(Chart));

export default {
    name: 'CrisisCallsByMonth',
    props: {
        data: {
            type: Object,
            default: () => null
        },
        loading: {
            type: Boolean,
            default: () => false
        }
    },
    data() {
        return {
            chartOptions: {
                animation: {
                    easing: 'easeOutQuad',
                    duration: 300
                },
                layout: {
                    padding: {
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0
                    }
                },
                scales: {
                    yAxes: [
                        {
                            gridLines: { display: false },
                            ticks: { fontSize: 15, fontColor: '#ffffff' }
                        }
                    ],
                    xAxes: [
                        {
                            gridLines: {
                                display: false,
                            },
                            ticks: {
                                fontSize: 15,
                                fontColor: '#ffffff',
                                callback(value) {
                                    return `${value}`;
                                },
                                precision: 0,
                            }
                        }
                    ]
                },
                tooltips: {
                    enabled: true,
                    callbacks: {
                        label: ({ value }) => `${value}`
                    }
                }
            },
            years: [],
            selectedYear: null,
            monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        }
    },
    computed: {
        cardClass() {
            if (this.loading) return 'dashboard-card chart-card skeleton progress';
            if (!this.monthData) return 'dashboard-card chart-card no-data';
            return 'dashboard-card chart-card';
        },
        monthData() {
            if (Object.keys(this.data).length === 0 || !this.selectedYear) {
                return false;
            }

            const organizedData = this.transformData(this.data);
            this.handleYears(organizedData);
            if (!this.years.includes(this.selectedYear)) {
                this.selectedYear = this.years[0];
            }

            return organizedData[this.selectedYear] || {};
        }
    },
    mounted() {
        const organizedData = this.transformData(this.data);
        this.handleYears(organizedData);
        this.selectedYear = this.years[0];
    },
    methods: {
        handleYears(organizedData) {
            this.years = Object.keys(organizedData).sort((a, b) => b - a);
        },
        transformData(data) {
            const result = {};

            Object.entries(data)
                .sort(([key1], [key2]) => key1.localeCompare(key2))
                .forEach(([key, value]) => {
                    const [year, monthNumber] = key.split('-');
                    const monthName = this.monthNames[parseInt(monthNumber, 10) - 1];

                    if (!result[year]) {
                        result[year] = {};
                    }

                result[year][monthName] = value;
            });

            return result;
        }
    }
}
</script>

<style lang="scss" scoped>
.chart-card {
    width: 100%;
    padding: 0;

    &.no-data {
        opacity: 0.5;
    }

    .header {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: stretch;
        margin-bottom: 1rem;
        padding: 1.5rem 1.25rem 0 1.24rem;

        .title {
            font-size: 16px;
            font-weight: 500;
            letter-spacing: 0.01rem;
            margin: 0 0 0.75rem 0;
            text-transform: none;
        }

        .year-button {
            margin-right: 5px;
            transition: background-color 0.3s ease;
            
            .selected {
                background-color: rgba(64, 121, 157, 1);
                color: $white;
                border: 0;
            }
        }
    }
    .root {
        padding: 0 2.45rem;
        height: 250px;
    }
}
</style>
